import { useState, useEffect } from 'react';
import axios from 'axios';
import { useWindowWidth } from '../hooks/useWindowWidth';

export default function SponsorsPage() {
  const width = useWindowWidth();
  const isMobile = width < 768;
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editWebsite, setEditWebsite] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { id, name }

  useEffect(() => { fetchSponsors(); }, []);

  const fetchSponsors = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/sponsors');
      setSponsors(res.data);
    } catch {
      setError('Fout bij laden.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(''); setMessage('');
    if (!name) return setError('Naam is verplicht.');
    try {
      await axios.post('/api/sponsors', { name, website });
      setName(''); setWebsite('');
      setMessage('Sponsor toegevoegd!');
      fetchSponsors();
    } catch (err) {
      setError(err.response?.data?.error || 'Fout bij toevoegen.');
    }
  };

  const handleEdit = (sponsor) => {
    setEditId(sponsor.id);
    setEditName(sponsor.name);
    setEditWebsite(sponsor.website || '');
  };

  const handleUpdate = async (id) => {
    setError(''); setMessage('');
    try {
      await axios.put(`/api/sponsors/${id}`, { name: editName, website: editWebsite });
      setEditId(null);
      setMessage('Sponsor bijgewerkt!');
      fetchSponsors();
    } catch (err) {
      setError(err.response?.data?.error || 'Fout bij bijwerken.');
    }
  };

  const handleDeleteConfirmed = async () => {
    const { id } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      await axios.delete(`/api/sponsors/${id}`);
      setMessage('Sponsor verwijderd.');
      fetchSponsors();
    } catch {
      setError('Fout bij verwijderen.');
    }
  };

  return (
    <div>
      {message && <div style={s.successMsg}>{message}</div>}
      {error && <div style={s.errorMsg}>{error}</div>}

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <div style={s.modalTitle}>Sponsor verwijderen</div>
            <p style={s.modalText}>
              Weet je zeker dat je <strong>{deleteConfirm.name}</strong> wilt verwijderen?
            </p>
            <div style={s.modalActions}>
              <button style={s.modalCancelBtn} onClick={() => setDeleteConfirm(null)}>
                Annuleren
              </button>
              <button style={s.modalDeleteBtn} onClick={handleDeleteConfirmed}>
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add sponsor form */}
      <div style={s.addCard}>
        <div style={s.addTitle}>Sponsor toevoegen</div>
        <form onSubmit={handleAdd} style={{ ...s.addForm, flexDirection: isMobile ? 'column' : 'row' }}>
          <input
            style={s.input}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Naam sponsor (bv. R&P Verzekeringen)"
          />
          <input
            style={s.input}
            value={website}
            onChange={e => setWebsite(e.target.value)}
            placeholder="Website (bv. https://rnpgroep.nl)"
          />
          <button type="submit" style={{ ...s.addBtn, minHeight: 44, ...(isMobile ? { width: '100%' } : {}) }}>+ Toevoegen</button>
        </form>
      </div>

      {/* Sponsors list */}
      {loading ? (
        <div style={s.empty}>Laden...</div>
      ) : sponsors.length === 0 ? (
        <div style={s.empty}>Nog geen sponsoren toegevoegd.</div>
      ) : (
        sponsors.map(sp => (
          <div key={sp.id} style={s.card}>
            {editId === sp.id ? (
              <div style={s.editWrap}>
                <input
                  style={s.input}
                  value={editName}
                  onChange={e => setEditName(e.target.value)}
                  placeholder="Naam"
                />
                <input
                  style={s.input}
                  value={editWebsite}
                  onChange={e => setEditWebsite(e.target.value)}
                  placeholder="Website"
                />
                <div style={s.editActions}>
                  <button style={s.saveBtn} onClick={() => handleUpdate(sp.id)}>Opslaan</button>
                  <button style={s.cancelBtn} onClick={() => setEditId(null)}>Annuleren</button>
                </div>
              </div>
            ) : (
              <>
                <div style={s.cardInfo}>
                  <div style={s.cardName}>{sp.name}</div>
                  {sp.website && (
                    <a href={sp.website} target="_blank" rel="noreferrer" style={s.cardWebsite}>
                      {sp.website}
                    </a>
                  )}
                </div>
                <div style={s.cardActions}>
                  <button style={s.editBtn} onClick={() => handleEdit(sp)}>Bewerken</button>
                  <button style={s.deleteBtn} onClick={() => setDeleteConfirm({ id: sp.id, name: sp.name })}>Verwijderen</button>
                </div>
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

const s = {
  addCard: {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    marginBottom: 20, border: '1px solid #ede9e3',
  },
  addTitle: { fontSize: 14, fontWeight: 600, color: '#1a2a1e', marginBottom: 14 },
  addForm: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  input: {
    flex: 1, minWidth: 180, padding: '10px 12px', border: '1px solid #e0dcd6',
    borderRadius: 8, fontSize: 14, color: '#1a2a1e', background: '#faf8f5',
    fontFamily: 'inherit', outline: 'none',
  },
  addBtn: {
    background: '#3a7a4a', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', whiteSpace: 'nowrap',
  },
  card: {
    background: '#fff', borderRadius: 12, padding: '16px 24px', marginBottom: 10,
    border: '1px solid #ede9e3', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
  },
  cardInfo: { flex: 1 },
  cardName: { fontSize: 15, fontWeight: 600, color: '#1a2a1e', marginBottom: 2 },
  cardWebsite: { fontSize: 12, color: '#3a7a4a', textDecoration: 'none' },
  cardActions: { display: 'flex', gap: 8 },
  editBtn: {
    background: '#fff', border: '1px solid #e0dcd6', borderRadius: 6,
    padding: '7px 14px', fontSize: 13, color: '#444', cursor: 'pointer', fontFamily: 'inherit',
  },
  deleteBtn: {
    background: '#fff', border: '1px solid #ffcccc', borderRadius: 6,
    padding: '7px 14px', fontSize: 13, color: '#cc0000', cursor: 'pointer', fontFamily: 'inherit',
  },
  editWrap: { display: 'flex', gap: 10, flex: 1, flexWrap: 'wrap', alignItems: 'center' },
  editActions: { display: 'flex', gap: 8 },
  saveBtn: {
    background: '#3a7a4a', color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    background: '#fff', border: '1px solid #e0dcd6', borderRadius: 6,
    padding: '8px 16px', fontSize: 13, color: '#666', cursor: 'pointer', fontFamily: 'inherit',
  },
  successMsg: {
    background: '#f0fff4', border: '1px solid #b8e6c4', borderRadius: 8,
    padding: '12px 16px', fontSize: 14, color: '#2d6a3f', marginBottom: 16,
  },
  errorMsg: {
    background: '#fff0f0', border: '1px solid #ffcccc', borderRadius: 8,
    padding: '12px 16px', fontSize: 14, color: '#cc0000', marginBottom: 16,
  },
  empty: { textAlign: 'center', color: '#aaa', padding: '40px', background: '#fff', borderRadius: 12 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    background: '#fff', borderRadius: 16, padding: '32px 36px',
    maxWidth: 420, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 'bold',
    color: '#1a2a1e', marginBottom: 12,
  },
  modalText: { fontSize: 15, color: '#555', lineHeight: 1.6, margin: '0 0 24px' },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
  modalCancelBtn: {
    background: '#fff', border: '1px solid #e0dcd6', borderRadius: 8,
    padding: '10px 20px', fontSize: 14, color: '#666', cursor: 'pointer', fontFamily: 'inherit',
  },
  modalDeleteBtn: {
    background: '#cc0000', color: '#fff', border: 'none', borderRadius: 8,
    padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  },
};
